import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input, Select, Spinner } from '../../components';
import { getCurrentUser, setCurrentUser } from '../../data/session';
import { getMe, updateMe } from '../../services/user-service';
import { logout, friendlyAuthError, routeAfterAuth } from '../../services/auth-service';

const STUDENT_COUNTS = ['<10', '10–50', '50–200', '200+'];
const ASSOC_PURPOSES = ['Academic research', 'Policy analysis', 'Ecosystem reporting', 'Quality control', 'Other'];
const CHECK_SIZES    = ['Angel (<$50k)', 'Seed ($50k–$500k)', 'Series A ($500k–$5M)', 'Later stage'];

type Role = 'partner' | 'associate' | 'investor';

interface FormState {
  // Partner
  institution: string;
  partnerTitle: string;
  studentCount: string;
  // Associate
  organization: string;
  associateTitle: string;
  purpose: string;
  // Investor
  fundName: string;
  investorTitle: string;
  checkSize: string;
}

const EMPTY: FormState = {
  institution: '', partnerTitle: '', studentCount: '',
  organization: '', associateTitle: '', purpose: '',
  fundName: '', investorTitle: '', checkSize: '',
};

const ROLE_HEADLINE: Record<Role, string> = {
  partner:   'Tell us about your institution',
  associate: 'Tell us about your organization',
  investor:  'Tell us about your fund',
};
const ROLE_TAGLINE: Record<Role, string> = {
  partner:   "We tailor your dashboard to your students' incubation journey.",
  associate: 'We tailor the cohort data we surface to the work you do.',
  investor:  'We curate pitches that fit the deals you typically write.',
};

const CompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const me = getCurrentUser();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const role = me.role as Role;
  const needsCompletion = role === 'partner' || role === 'associate' || role === 'investor';

  // Bootstrap: if no session, login. If role doesn't need this page, route them onward.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!me.id) {
        navigate('/login');
        return;
      }
      try {
        const profile = await getMe();
        if (!alive) return;
        // Hand the latest backend snapshot to routeAfterAuth so we don't bounce back here.
        const target = routeAfterAuth(profile as any);
        if (target !== '/complete-profile') {
          navigate(target);
          return;
        }
        if (needsCompletion) {
          const partner   = (profile as any).partner   || {};
          const associate = (profile as any).associate || {};
          const investor  = (profile as any).investor  || {};
          setForm({
            institution:    partner.institution   || '',
            partnerTitle:   partner.titleRole     || '',
            studentCount:   partner.studentCount  || '',
            organization:   associate.organization|| '',
            associateTitle: associate.titleRole   || '',
            purpose:        associate.purpose     || '',
            fundName:       investor.fundName     || '',
            investorTitle:  investor.titleRole    || '',
            checkSize:      investor.checkSize    || '',
          });
        }
      } catch (err: any) {
        if (alive) setError(friendlyAuthError(err) || 'Could not load your profile.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k: keyof FormState, v: string) => {
    setForm(prev => ({ ...prev, [k]: v }));
    if (errors[k]) {
      const n = { ...errors };
      delete n[k];
      setErrors(n);
    }
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (role === 'partner') {
      if (!form.institution.trim()) errs.institution = 'Required';
      if (!form.partnerTitle.trim()) errs.partnerTitle = 'Required';
      if (!form.studentCount) errs.studentCount = 'Required';
    } else if (role === 'associate') {
      if (!form.organization.trim()) errs.organization = 'Required';
      if (!form.associateTitle.trim()) errs.associateTitle = 'Required';
      if (!form.purpose) errs.purpose = 'Required';
    } else if (role === 'investor') {
      if (!form.fundName.trim()) errs.fundName = 'Required';
      if (!form.investorTitle.trim()) errs.investorTitle = 'Required';
      if (!form.checkSize) errs.checkSize = 'Required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: any = {};
      if (role === 'partner') {
        payload.partner = {
          institution: form.institution.trim(),
          titleRole: form.partnerTitle.trim(),
          studentCount: form.studentCount,
        };
      } else if (role === 'associate') {
        payload.associate = {
          organization: form.organization.trim(),
          titleRole: form.associateTitle.trim(),
          purpose: form.purpose,
        };
      } else if (role === 'investor') {
        payload.investor = {
          fundName: form.fundName.trim(),
          titleRole: form.investorTitle.trim(),
          checkSize: form.checkSize,
        };
      }
      const updated = await updateMe(payload);
      setCurrentUser(updated);
      // After saving, recompute where they belong. Most go to /pending now.
      const target = routeAfterAuth(updated as any);
      navigate(target === '/complete-profile' ? '/pending' : target);
    } catch (err: any) {
      setError(friendlyAuthError(err) || err?.message || 'Could not save — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6">
        <Spinner className="w-8 h-8 text-chart" />
      </div>
    );
  }

  if (!needsCompletion) {
    // Defensive — bootstrap should already have routed.
    return null;
  }

  // Role guarantees TS knows it's one of three.
  const headline = ROLE_HEADLINE[role];
  const tagline  = ROLE_TAGLINE[role];

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Top bar — logo + sign out */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-line bg-shell">
        <Link to="/" className="inline-flex items-center">
          <img src="/logo.png" alt="BunyadKar" className="h-8 w-auto opacity-80" />
        </Link>
        <button
          type="button"
          onClick={handleSignOut}
          className="text-sm text-muted hover:text-text transition-colors"
        >
          Sign out
        </button>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 mb-3 px-3 py-1 rounded-full bg-chart/10 text-chart text-xs font-medium tracking-wide uppercase">
              Last step
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-wide text-text uppercase mb-2">
              Let's set up your account
            </h1>
            <p className="font-serif-display text-lg italic text-muted">
              {headline}.
            </p>
            <p className="text-sm text-muted mt-2">{tagline}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {role === 'partner' && (
              <>
                <Input
                  label="Institution name"
                  value={form.institution}
                  onChange={e => set('institution', e.target.value)}
                  placeholder="e.g. Institute of Business Administration"
                  error={errors.institution}
                  maxLength={80}
                />
                <Input
                  label="Your title / role"
                  value={form.partnerTitle}
                  onChange={e => set('partnerTitle', e.target.value)}
                  placeholder="e.g. Head of Incubation"
                  error={errors.partnerTitle}
                  maxLength={80}
                />
                <Select
                  label="Approx. student founders"
                  placeholder="Select…"
                  value={form.studentCount}
                  onChange={e => set('studentCount', e.target.value)}
                  options={STUDENT_COUNTS.map(o => ({ value: o, label: o }))}
                  error={errors.studentCount}
                />
              </>
            )}

            {role === 'associate' && (
              <>
                <Input
                  label="Organization name"
                  value={form.organization}
                  onChange={e => set('organization', e.target.value)}
                  placeholder="e.g. Pakistan Policy Lab"
                  error={errors.organization}
                  maxLength={80}
                />
                <Input
                  label="Role / title"
                  value={form.associateTitle}
                  onChange={e => set('associateTitle', e.target.value)}
                  placeholder="e.g. Senior Researcher"
                  error={errors.associateTitle}
                  maxLength={80}
                />
                <Select
                  label="Purpose of access"
                  placeholder="Select…"
                  value={form.purpose}
                  onChange={e => set('purpose', e.target.value)}
                  options={ASSOC_PURPOSES.map(o => ({ value: o, label: o }))}
                  error={errors.purpose}
                />
              </>
            )}

            {role === 'investor' && (
              <>
                <Input
                  label="Fund / firm name"
                  value={form.fundName}
                  onChange={e => set('fundName', e.target.value)}
                  placeholder="e.g. Indus Seed Partners"
                  error={errors.fundName}
                  maxLength={80}
                />
                <Input
                  label="Role / title"
                  value={form.investorTitle}
                  onChange={e => set('investorTitle', e.target.value)}
                  placeholder="e.g. Principal"
                  error={errors.investorTitle}
                  maxLength={80}
                />
                <Select
                  label="Typical check size"
                  placeholder="Select…"
                  value={form.checkSize}
                  onChange={e => set('checkSize', e.target.value)}
                  options={CHECK_SIZES.map(o => ({ value: o, label: o }))}
                  error={errors.checkSize}
                />
              </>
            )}

            {error && <p className="text-danger text-sm">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-chart text-white py-3 rounded-card font-medium hover:bg-chart/90 transition-all duration-200 shadow-sm text-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting && <Spinner className="w-4 h-4" />}
              {submitting ? 'Saving…' : 'Finish setup'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
